import { Placement } from "@popperjs/core";
import React, { RefAttributes, useEffect, useState } from "react";
import { usePopper } from "react-popper";

import { useStabilized } from "../../hooks/useStabilized";

interface Props {
  popper?: React.ReactElement;
  placement?: Placement;
  /** Callback to get the refElement's node value */
  refElementRef?: (el: HTMLElement) => void;
  /** Callback to get the popperElement's node value */
  popperElementRef?: (el: HTMLElement) => void;
  /** Manually assign the reference element */
  refElement?: HTMLElement | null;
}

type RefType = HTMLElement | null;

const Position: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const [refElement, setRefElement] = useState<RefType>(null);
  const [popperElement, setPopperElement] = useState<RefType>(null);
  const { styles, attributes } = usePopper(
    props.refElement || refElement,
    popperElement,
    {
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, 5],
          },
        },
      ],
      placement: props.placement || "bottom-start",
    },
  );

  const refElementRef = useStabilized(props.refElementRef, [refElement]);
  const popperElementRef = useStabilized(props.popperElementRef, [
    popperElement,
  ]);

  useEffect(() => {
    refElementRef?.(refElement as HTMLElement);
  }, [refElementRef, refElement]);

  useEffect(() => {
    popperElementRef?.(popperElement as HTMLElement);
  }, [popperElementRef, popperElement]);

  if (!React.isValidElement<RefAttributes<HTMLElement>>(props.children)) {
    return null;
  }

  return (
    <>
      {React.cloneElement(props.children, {
        ref: setRefElement,
      })}
      {props.popper &&
        React.cloneElement(props.popper, {
          ref: setPopperElement,
          style: styles.popper,
          ...attributes.popper,
        })}
    </>
  );
};

export default Position;
